import type {
  AttentionContent,
  AttentionMessage,
  AttentionQuote,
  AttentionQuotePreviewKind,
} from "./attention-message";

const UNKNOWN_SENDER_LABEL = "Unknown";

export function deriveQuotePreviewKind(
  content: AttentionContent,
): AttentionQuotePreviewKind {
  switch (content.type) {
    case "text":
    case "supervision":
      return "text";
    case "gif":
      return "gif";
    case "media":
      return content.media.mediaType;
    case "system":
      return "system";
    case "activity":
    case "entityShared":
    case "entityInteraction":
      return "entity";
    case "game":
    case "hidden":
    case "takedown":
    case "unsupported":
      return "other";
    default: {
      const exhaustive: never = content;
      return exhaustive;
    }
  }
}

export function deriveQuotePreviewText(content: AttentionContent): string {
  switch (content.type) {
    case "text":
    case "supervision":
      return content.text;
    case "gif":
      return "GIF";
    case "media":
      switch (content.media.mediaType) {
        case "image":
          return "Photo";
        case "video":
          return "Video";
        case "audio":
          return "Audio";
        default: {
          const exhaustive: never = content.media;
          return exhaustive;
        }
      }
    case "system":
      return content.text;
    case "activity":
    case "entityShared":
    case "entityInteraction":
      return content.text || "Update";
    case "game":
      return "Game";
    case "hidden":
      return "Hidden message";
    case "takedown":
      return "Removed message";
    case "unsupported":
      return content.text;
    default: {
      const exhaustive: never = content;
      return exhaustive;
    }
  }
}

export function deriveQuotePreviewThumbnail(
  content: AttentionContent,
): string | null {
  if (content.type === "gif") return content.url;
  if (content.type === "media") {
    switch (content.media.mediaType) {
      case "image":
        return content.media.thumbnailUrl ?? content.media.url;
      case "video":
        return content.media.posterUrl;
      case "audio":
        return null;
      default: {
        const exhaustive: never = content.media;
        return exhaustive;
      }
    }
  }
  return null;
}

// Attention-owned reply preview: the send adapter maps it back to `parentMessageId`,
// so no Sendbird types cross this boundary.
export function deriveAttentionQuote(message: AttentionMessage): AttentionQuote {
  return {
    id: message.id,
    senderLabel:
      message.sender?.displayName ??
      message.sender?.nickname ??
      UNKNOWN_SENDER_LABEL,
    previewKind: deriveQuotePreviewKind(message.content),
    text: deriveQuotePreviewText(message.content),
    thumbnailUrl: deriveQuotePreviewThumbnail(message.content),
  };
}

// Sendbird QUOTE_REPLY rejects nested parents. Replying to a reply attaches
// to the original message instead of the reply itself.
export function resolveSendableQuote(message: AttentionMessage): AttentionQuote {
  if (message.quote) return message.quote;
  return deriveAttentionQuote(message);
}
